import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import {
  LayoutDashboard, Package, ShoppingCart, ShoppingBag, 
  Users, Building2, FileText, BarChart3, Wallet,
  CreditCard, DollarSign, ChevronLeft, ChevronRight,
  Settings, ChevronDown, Boxes, Truck, ClipboardList,
  Receipt, Bell, Shield, BookOpen, BookOpenText,
  ChevronUp, X, Warehouse
} from 'lucide-react';

const menuGroups = [
  {
    label: 'Menu Utama',
    roles: ['owner', 'inventory_admin', 'sales', 'purchasing', 'finance'],
    items: [
      { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, permission: 'view_dashboard' },
      { name: 'Panduan', path: '/guide', icon: BookOpenText },
    ],
  },
  {
    label: 'Master Data',
    roles: ['owner', 'inventory_admin', 'purchasing', 'finance'],
    items: [
      { name: 'Produk', path: '/products', icon: Package, permission: 'view_products' },
      { name: 'Kategori', path: '/categories', icon: Boxes, permission: 'view_categories' },
      { name: 'Satuan', path: '/units', icon: Building2, permission: 'view_units' },
      { name: 'Gudang', path: '/warehouses', icon: Warehouse, permission: 'view_warehouses' },
      { name: 'Supplier', path: '/suppliers', icon: Truck, permission: 'view_suppliers' },
      { name: 'Pelanggan', path: '/customers', icon: Users, permission: 'view_customers' },
    ],
  },
  {
    label: 'Inventory',
    roles: ['owner', 'inventory_admin'],
    items: [
      { name: 'Stok', path: '/inventory/stock', icon: ClipboardList, permission: 'view_inventory' },
      { name: 'Barang Masuk', path: '/inventory/in', icon: ChevronDown, permission: 'view_goods_receipts' },
      { name: 'Barang Keluar', path: '/inventory/out', icon: ChevronUp, permission: 'view_inventory' },
      { name: 'Transfer Stok', path: '/inventory/transfer', icon: Package, permission: 'view_inventory' },
      { name: 'Stock Opname', path: '/inventory/opname', icon: ClipboardList, permission: 'view_stock_opname' },
      { name: 'Kartu Stok', path: '/inventory/stock-card', icon: BookOpen, permission: 'view_inventory' },
    ],
  },
  {
    label: 'Pembelian',
    roles: ['owner', 'purchasing', 'finance'],
    items: [
      { name: 'Purchase Request', path: '/purchasing/requests', icon: ShoppingBag, permission: 'view_purchase_requests' },
      { name: 'Purchase Order', path: '/purchasing/orders', icon: ShoppingCart, permission: 'view_purchase_orders' },
      { name: 'Penerimaan Barang', path: '/purchasing/receipts', icon: Truck, permission: 'view_goods_receipts' },
      { name: 'Invoice Pembelian', path: '/purchasing/invoices', icon: Receipt, permission: 'view_purchases' },
      { name: 'Retur Pembelian', path: '/purchasing/returns', icon: ShoppingCart, permission: 'view_purchase_returns' },
    ],
  },
  {
    label: 'Penjualan',
    roles: ['owner', 'sales', 'finance'],
    items: [
      { name: 'Transaksi Baru', path: '/sales/new', icon: ShoppingCart, permission: 'create_sales' },
      { name: 'Riwayat Penjualan', path: '/sales/history', icon: ShoppingBag, permission: 'view_sales' },
      { name: 'Invoice', path: '/sales/invoices', icon: Receipt, permission: 'view_sales_invoices' },
      { name: 'Retur Penjualan', path: '/sales/returns', icon: ShoppingCart, permission: 'view_sales_returns' },
      { name: 'Piutang', path: '/sales/receivables', icon: DollarSign, permission: 'view_accounts_receivable' },
    ],
  },
  {
    label: 'Keuangan',
    roles: ['owner', 'finance'],
    items: [
      { name: 'Kas & Bank', path: '/finance/cash-bank', icon: Wallet, permission: 'view_cash_bank' },
      { name: 'Utang Usaha', path: '/finance/payables', icon: CreditCard, permission: 'view_accounts_payable' },
      { name: 'Piutang Usaha', path: '/finance/receivables', icon: DollarSign, permission: 'view_accounts_receivable' },
      { name: 'Chart of Accounts', path: '/finance/accounts', icon: BookOpen, permission: 'view_accounts' },
      { name: 'Jurnal', path: '/finance/journals', icon: FileText, permission: 'view_journals' },
      { name: 'Periode Akuntansi', path: '/finance/periods', icon: CalendarIcon, permission: 'view_journals' },
    ],
  },
  {
    label: 'Laporan',
    roles: ['owner', 'finance'],
    items: [
      { name: 'Laba Rugi', path: '/reports/profit-loss', icon: BarChart3, permission: 'view_reports' },
      { name: 'Neraca', path: '/reports/balance-sheet', icon: BarChart3, permission: 'view_reports' },
      { name: 'Perubahan Modal', path: '/reports/equity', icon: BarChart3, permission: 'view_reports' },
      { name: 'Buku Besar', path: '/reports/general-ledger', icon: BookOpen, permission: 'view_reports' },
      { name: 'Neraca Saldo', path: '/reports/trial-balance', icon: FileText, permission: 'view_reports' },
      { name: 'Laporan Penjualan', path: '/reports/sales', icon: ShoppingBag, permission: 'view_reports' },
      { name: 'Laporan Pembelian', path: '/reports/purchases', icon: ShoppingCart, permission: 'view_reports' },
      { name: 'Laporan Persediaan', path: '/reports/inventory', icon: Package, permission: 'view_reports' },
      { name: 'Aging Piutang', path: '/reports/aging-receivable', icon: DollarSign, permission: 'view_reports' },
      { name: 'Aging Utang', path: '/reports/aging-payable', icon: CreditCard, permission: 'view_reports' },
    ],
  },
  {
    label: 'Sistem',
    roles: ['owner', 'finance', 'inventory_admin', 'purchasing', 'sales'],
    items: [
      { name: 'Pengguna', path: '/users', icon: Users, permission: 'view_users' },
      { name: 'Roles', path: '/roles', icon: Shield, permission: 'view_roles' },
      { name: 'Notifikasi', path: '/notifications', icon: Bell, permission: 'view_notifications' },
      { name: 'Audit Log', path: '/audit-logs', icon: FileText, permission: 'view_audit_logs' },
      { name: 'Pengaturan', path: '/settings', icon: Settings, permission: 'view_settings' },
      { name: 'Panduan', path: '/guide', icon: BookOpenText },
    ],
  },
];

function CalendarIcon(props) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/>
    </svg>
  );
}

function ChevronDownIcon(props) {
  return <ChevronDown size={14} {...props} />;
}

export default function Sidebar() {
  const { user } = useAuth();
  const { isCollapsed, toggleSidebar, isMobileOpen, closeMobileSidebar, isMobile } = useSidebar();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState(() => {
    return ['Menu Utama', 'Laporan', 'Sistem'];
  });

  const toggleMenu = (label) => {
    if (!isMobile && isCollapsed) return;
    setExpandedMenus(prev => 
      prev.includes(label) ? prev.filter(m => m !== label) : [...prev, label]
    );
  };

  // Auto-close sidebar after navigation on narrow screens
  useEffect(() => {
    if (isMobile && isMobileOpen) {
      closeMobileSidebar();
    }
  }, [location.pathname]);

  const role = user?.role?.name || 'owner';
  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === path;
    // Fix: Check exact match or if current path starts with path + '/' (prevents Stock matching Stock Card)
    const exactOrSubpath = location.pathname === path || 
      (path !== '/' && location.pathname.startsWith(path + '/'));
    return exactOrSubpath;
  };

  // Menu visibility follows the permissions the API enforces, so a role never
  // sees a menu whose endpoint answers 403. Owners hold every permission.
  const isOwner = role === 'owner';
  const canSee = (permission) =>
    !permission || isOwner || Boolean(user?.permissions?.includes(permission));

  const filteredGroups = menuGroups
    .filter((group) => group.roles.includes(role))
    .map((group) => ({ ...group, items: group.items.filter((item) => canSee(item.permission)) }))
    .filter((group) => group.items.length > 0);

  // Narrow-screen sidebar: always show expanded, never collapsed
  const showCollapsed = !isMobile && isCollapsed;

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && (
        <div 
          className={`fixed inset-0 z-40 bg-surface-950/35 backdrop-blur-[2px] transition-opacity duration-300 ${
            isMobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`} 
          onClick={closeMobileSidebar} 
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 flex h-screen flex-col
        border-r border-white/70 bg-white/60 shadow-sidebar backdrop-blur-2xl dark:border-white/10 dark:bg-surface-950/70
        transition-all duration-300 ease-soft
        ${isMobile
          ? `${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}`
          : `${showCollapsed ? 'w-[76px]' : 'w-[264px]'}`
        }
      `}>
        {/* Logo */}
        <div className={`flex h-16 flex-shrink-0 items-center border-b border-surface-100 dark:border-surface-800 ${showCollapsed ? 'justify-center px-3' : 'justify-between px-5'}`}>
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary-600 shadow-glow">
              <span className="text-sm font-semibold text-primary-900">S</span>
            </div>
            {!showCollapsed && (
              <div>
                <span className="block text-[15px] font-semibold tracking-tight text-surface-900 dark:text-white">Stokku</span>
                <p className="mt-0.5 text-[10px] leading-none tracking-wide text-surface-400 dark:text-surface-500">Akuntansi &amp; Inventory</p>
              </div>
            )}
          </div>
          {!isMobile && (
            <button
              onClick={toggleSidebar}
              className="hidden rounded-full p-1.5 text-surface-400 transition-colors hover:bg-surface-100 hover:text-surface-700 dark:text-surface-500 dark:hover:bg-surface-800 dark:hover:text-surface-200 lg:flex"
              title={showCollapsed ? 'Perluas sidebar' : 'Perkecil sidebar'}
            >
              {showCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          )}
          {isMobile && (
            <button onClick={closeMobileSidebar} className="rounded-full p-1.5 text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Menu */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 scrollbar-thin">
          {filteredGroups.map((group) => {
            const isExpanded = expandedMenus.includes(group.label);

            return (
              <div key={group.label} className="mb-4">
                {/* Group Header */}
                {!showCollapsed && (
                  <button
                    onClick={() => toggleMenu(group.label)}
                    className={`mb-1 flex w-full items-center justify-between px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                      isExpanded
                        ? 'text-surface-500 dark:text-surface-400'
                        : 'text-surface-400 dark:text-surface-500'
                    } hover:text-surface-700 dark:hover:text-surface-200`}
                  >
                    <span>{group.label}</span>
                    <ChevronDownIcon className={`transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
                  </button>
                )}

                {/* Menu Items */}
                {(isExpanded || showCollapsed) && (
                  <div className="space-y-0.5">
                    {group.items.map((item) => {
                      const active = isActive(item.path);
                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          end={item.path === '/dashboard'}
                          onClick={() => { if (isMobile) closeMobileSidebar(); }}
                          className={`nav-item ${
                            active
                              ? 'bg-primary-600 font-semibold text-primary-900 shadow-[0_6px_18px_-10px_rgba(194,224,80,0.95)] dark:bg-primary-600/90 dark:text-primary-950'
                              : 'text-surface-500 hover:bg-white/70 hover:text-surface-900 dark:text-surface-400 dark:hover:bg-white/10 dark:hover:text-surface-100'
                          } ${showCollapsed ? 'mx-auto w-11 justify-center px-0' : ''}`}
                          title={showCollapsed ? item.name : ''}
                        >
                          <item.icon
                            size={19}
                            strokeWidth={active ? 2.1 : 1.75}
                            className={`flex-shrink-0 ${active ? 'text-primary-900' : ''}`}
                          />
                          {!showCollapsed && <span className="truncate">{item.name}</span>}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User info at bottom */}
        <div className={`flex-shrink-0 border-t border-surface-100 dark:border-surface-800 ${showCollapsed ? 'p-2.5' : 'p-3'}`}>
          {showCollapsed ? (
            <NavLink to="/profile" className="flex justify-center" title="Profil">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-50 ring-1 ring-primary-200/70 transition-all hover:ring-primary-300 dark:bg-primary-950 dark:ring-primary-800">
                <span className="text-xs font-semibold text-primary-900 dark:text-primary-300">
                  {user?.fullName?.charAt(0) || 'U'}
                </span>
              </div>
            </NavLink>
          ) : (
            <NavLink to="/profile" className="flex items-center gap-3 rounded-xl border border-transparent p-2 transition-colors hover:border-surface-200 hover:bg-surface-50 dark:hover:border-surface-700 dark:hover:bg-surface-900">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 ring-1 ring-primary-200/70 dark:bg-primary-950 dark:ring-primary-800">
                <span className="text-sm font-semibold text-primary-900 dark:text-primary-300">
                  {user?.fullName?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium leading-tight text-surface-900 dark:text-white">{user?.fullName || 'User'}</p>
                <p className="mt-0.5 truncate text-[11px] leading-tight text-surface-400 dark:text-surface-500">{user?.role?.displayName}</p>
              </div>
              <ChevronRight size={14} className="flex-shrink-0 text-surface-300 dark:text-surface-600" />
            </NavLink>
          )}
        </div>
      </aside>
    </>
  );
}
